'use client'

import debounce from 'lodash/debounce'
import { MapPin } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'
import { Skeleton } from '../ui/skeleton'
import IconInput from './icon-input'
import type { AddressPrediction } from '@/hooks/useAddressSearch'
import { useAddressDetails, useAddressSearch } from '@/hooks/useAddressSearch'
import { cn } from '@/lib/utils'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command'

const provinceMap: Record<string, string> = {
  Alberta: 'AB',
  'British Columbia': 'BC',
  Manitoba: 'MB',
  'New Brunswick': 'NB',
  'Newfoundland and Labrador': 'NL',
  'Northwest Territories': 'NT',
  'Nova Scotia': 'NS',
  Nunavut: 'NU',
  Ontario: 'ON',
  'Prince Edward Island': 'PE',
  Quebec: 'QC',
  Saskatchewan: 'SK',
  Yukon: 'YT',
} as const

export interface ParsedAddress {
  street: string
  city: string
  province: string
  postal: string
}

interface AddressInputProps {
  field: any // TanStack Form field
  onAddressSelect?: (parsedAddress: ParsedAddress) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  label?: string
  required?: boolean
}

const parseSearchTermAddress = (searchTerm: string): ParsedAddress => {
  const [street, city, fullProvince, postalCode] = searchTerm
    .split(',')
    .map((part) => part.trim())
  const province = provinceMap[fullProvince] || fullProvince
  let postal = postalCode || ''
  if (postal && postal.length === 6) {
    postal = `${postal.slice(0, 3)} ${postal.slice(3)}`
  }

  return {
    street: street || '',
    city: city || '',
    province: province || '',
    postal: postal || '',
  } as ParsedAddress
}

const getDisplayValue = (value: string | ParsedAddress): string => {
  if (typeof value === 'string') return value
  if (typeof value === 'object') {
    return formatAddress(value)
  }
  return ''
}

const formatAddress = (parsed: ParsedAddress): string => {
  const parts: Array<string> = []
  if (parsed.street) parts.push(parsed.street)
  if (parsed.city) parts.push(parsed.city)
  if (parsed.province) parts.push(parsed.province)
  if (parsed.postal) parts.push(parsed.postal)
  return parts.join(', ')
}

export function AddressInput({
  field,
  onAddressSelect,
  placeholder = 'Start typing an address...',
  className,
  disabled = false,
  label,
  required = false,
}: AddressInputProps) {
  const initialValue = formatAddress(field.state.value || {})
  const [inputValue, setInputValue] = useState(initialValue)
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialValue)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null)
  const isSelectingRef = useRef(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const { data: predictions = [], isPending: searchingAddress } =
    useAddressSearch(debouncedSearchTerm)

  const { data: addressDetails } = useAddressDetails(selectedPlaceId)

  const debouncedSetSearch = React.useMemo(
    () => debounce(setDebouncedSearchTerm, 1000),
    [],
  )

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel()
    }
  }, [debouncedSetSearch])

  useEffect(() => {
    if (addressDetails) {
      const parsed: ParsedAddress = {
        street: [
          addressDetails.address.house_number,
          addressDetails.address.road,
        ]
          .filter(Boolean)
          .join(' '),
        city:
          addressDetails.address.city ||
          addressDetails.address.town ||
          addressDetails.address.village ||
          '',
        province: addressDetails.address.province || '',
        postal: addressDetails.address.postcode || '',
      }

      field.handleChange(parsed)
      onAddressSelect?.(parsed)
      setInputValue(formatAddress(parsed))
      setDebouncedSearchTerm(formatAddress(parsed))
      setSelectedPlaceId(null)
      isSelectingRef.current = false
    }
  }, [addressDetails])

  return (
    <div className={cn('relative', className)}>
      <Popover
        open={showSuggestions}
        onOpenChange={setShowSuggestions}
        modal={false}
      >
        <PopoverTrigger asChild>
          <div>
            <IconInput
              ref={inputRef}
              icon={MapPin}
              field={field}
              label={label}
              required={required}
              id={field.name}
              name={field.name}
              value={getDisplayValue(inputValue)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value
                setInputValue(value)
                debouncedSetSearch(value)
                setShowSuggestions(true)
              }}
              onBlur={() => {
                setTimeout(() => {
                  setShowSuggestions(false)
                  // Only validate if not currently selecting an address
                  if (!isSelectingRef.current) {
                    onAddressSelect?.(parseSearchTermAddress(inputValue))
                    field.handleBlur()
                  }
                }, 200)
              }}
              onFocus={() => {
                if (inputValue.length >= 5 && predictions.length > 0) {
                  setShowSuggestions(true)
                }
              }}
              placeholder={placeholder}
              disabled={disabled}
              autoComplete="off"
            />
          </div>
        </PopoverTrigger>

        <PopoverContent
          className="w-(--radix-popover-trigger-width) p-0 z-50"
          align="start"
          side="bottom"
          sideOffset={4}
          onWheelCapture={(e) => e.stopPropagation()}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command>
            <CommandList
              className="max-h-50 overflow-y-auto overscroll-contain touch-pan-y"
              onWheelCapture={(e) => e.stopPropagation()}
            >
              {searchingAddress && debouncedSearchTerm.length >= 5 && (
                <div className="p-4 space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              )}

              {!searchingAddress &&
                predictions.length === 0 &&
                debouncedSearchTerm.length >= 5 && (
                  <CommandEmpty>No addresses found</CommandEmpty>
                )}

              {!searchingAddress && predictions.length > 0 && (
                <CommandGroup>
                  {predictions.map((prediction: AddressPrediction) => (
                    <CommandItem
                      key={prediction.place_id}
                      onSelect={() => {
                        isSelectingRef.current = true
                        setSelectedPlaceId(prediction.place_id)
                        setShowSuggestions(false)
                      }}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center w-full">
                        <MapPin className="mr-2 h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">
                            {prediction.main_text}
                          </div>
                          <div className="text-sm text-muted-foreground truncate">
                            {prediction.secondary_text}
                          </div>
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
